import React, { useEffect, useMemo } from 'react';
import { Card, Form, Input, Select, Space, message } from 'antd';
import type { QaqcPipeWps } from '../../../../types';
import { insertData, updateData, fetchSqlData } from '../../../../services/api';
import { useGetQaqcPipeServiceclass } from '../../../../hooks/genericHooks';
import Button from '../../../../components/common/Button';

export type QaqcPipeWpsMode = 'create' | 'edit';

interface QaqcPipeWpsCreateProps {
  mode?: QaqcPipeWpsMode;
  initialValues?: Partial<QaqcPipeWps> | null;
  onSaved?: (saved: QaqcPipeWps | any) => void;
  onCancel?: () => void;
  onBack?: () => void;
  viewOnly?: boolean;
}

type FormValues = {
  project_id?: number | string;
  wps_name: string;
  serviceclass_list: string[];
  wps_oversize1?: string | null;
  serviceclass_list_os1?: string[];
  wps_oversize2?: string | null;
  serviceclass_list_os2?: string[];
};

const QaqcPipeWpsCreate: React.FC<QaqcPipeWpsCreateProps> = ({
  mode = 'create',
  initialValues,
  onSaved,
  onCancel,
  onBack,
  viewOnly,
}) => {
  const [form] = Form.useForm<FormValues>();
  const isEdit = mode === 'edit';
  const isViewOnly = !!viewOnly;

  const { data: svcResp } = useGetQaqcPipeServiceclass();
  const serviceClassOptions = useMemo(() => {
    const rows: any[] = (svcResp?.data as any) || [];
    const set = new Set<string>();
    rows.forEach(r => {
      const val = String(
        r.serviceclass_name ?? r.service_class ?? r.serviceclass ?? r.name ?? ''
      ).trim();
      if (val) set.add(val);
    });
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map(v => ({ value: v, label: v }));
  }, [svcResp]);

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        project_id: initialValues.project_id ?? 1,
        wps_name: initialValues.wps_name,
        serviceclass_list: splitCSV(initialValues.serviceclass_list),
        wps_oversize1: initialValues.wps_oversize1 ?? undefined,
        serviceclass_list_os1: splitCSV(initialValues.serviceclass_list_os1),
        wps_oversize2: initialValues.wps_oversize2 ?? undefined,
        serviceclass_list_os2: splitCSV(initialValues.serviceclass_list_os2),
      } as any);
    } else {
      form.setFieldsValue({ project_id: 1 } as any);
    }
  }, [initialValues, form]);

  const splitCSV = (val?: string | null) => {
    if (!val) return [] as string[];
    return String(val)
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  };

  const joinCSV = (arr?: string[] | null) => {
    if (!arr) return null;
    return arr.filter(Boolean).join(',');
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload: any = {
        project_id: Number(values.project_id) || 1,
        wps_name: values.wps_name,
        serviceclass_list: joinCSV(values.serviceclass_list) || '',
        wps_oversize1: values.wps_oversize1 || null,
        serviceclass_list_os1: joinCSV(values.serviceclass_list_os1),
        wps_oversize2: values.wps_oversize2 || null,
        serviceclass_list_os2: joinCSV(values.serviceclass_list_os2),
      };

      if (isEdit && initialValues?.id) {
        await updateData('qaqc_pipe_wps', initialValues.id, payload, 'id');
        message.success('WPS updated');
        try { window.dispatchEvent(new Event('qaqc_pipe_wps:changed')); } catch {}
        onSaved?.({ id: initialValues.id, ...payload });
      } else {
        const resp = await insertData('qaqc_pipe_wps', payload);
        message.success('WPS created');
        try { window.dispatchEvent(new Event('qaqc_pipe_wps:changed')); } catch {}
        onSaved?.({ id: resp?.ids?.[0], ...payload });
        form.resetFields();
      }
    } catch (e: any) {
      if (e?.errorFields) return;
      const msg = e?.response?.data?.error || e?.message || 'Save failed';
      message.error(msg);
    }
  };

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 18 }}>
            {(isEdit || isViewOnly) && onBack && (
              <a onClick={onBack} style={{ marginRight: 16, fontSize: 16 }}>&larr; Back</a>
            )}
            {isViewOnly ? 'View WPS' : isEdit ? 'Edit WPS' : 'Create WPS'}
          </span>
        </div>
      }
      bordered
      size="small"
    >
      <Form form={form} layout="vertical" disabled={isViewOnly}>
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          <Space style={{ gap: 12, display: 'flex', flexWrap: 'wrap' }}>
            <Form.Item label="Project" name="project_id" rules={[{ required: true }]}
              style={{ marginBottom: 0 }}>
              <Input style={{ width: 120 }} />
            </Form.Item>

            <Form.Item label="WPS Name" name="wps_name" rules={[{ required: true, max: 10 }]}
              style={{ marginBottom: 0 }}>
              <Input maxLength={10} style={{ width: 160 }} />
            </Form.Item>

            <Form.Item label="Oversize 1" name="wps_oversize1" style={{ marginBottom: 0 }}>
              <Input maxLength={10} style={{ width: 160 }} />
            </Form.Item>

            <Form.Item label="Oversize 2" name="wps_oversize2" style={{ marginBottom: 0 }}>
              <Input maxLength={10} style={{ width: 160 }} />
            </Form.Item>
          </Space>

          <Form.Item label="Service Classes" name="serviceclass_list" rules={[{ required: true }]}>
            <Select
              mode="multiple"
              options={serviceClassOptions}
              placeholder="Select service classes"
              showSearch
              optionFilterProp="label"
              style={{ minWidth: 400 }}
            />
          </Form.Item>

          <Form.Item label="OS1 Classes" name="serviceclass_list_os1">
            <Select
              mode="multiple"
              options={serviceClassOptions}
              placeholder="Select service classes for oversize-1"
              showSearch
              optionFilterProp="label"
              style={{ minWidth: 400 }}
            />
          </Form.Item>

          <Form.Item label="OS2 Classes" name="serviceclass_list_os2">
            <Select
              mode="multiple"
              options={serviceClassOptions}
              placeholder="Select service classes for oversize-2"
              showSearch
              optionFilterProp="label"
              style={{ minWidth: 400 }}
            />
          </Form.Item>

          {!isViewOnly && (
            <Space>
              <Button action={isEdit ? 'edit' : 'add'} type="primary" onClick={onSubmit}>{isEdit ? 'Update' : 'Create'}</Button>
              {onCancel && <Button onClick={onCancel}>Cancel</Button>}
            </Space>
          )}
        </Space>
      </Form>
    </Card>
  );
};

export default QaqcPipeWpsCreate;
